import { z } from 'zod';
import {
  AllCharLabelSchema,
  AlphanumericLabelSchema,
  GradYearSchema,
  HttpUrlSchema,
  SlackChannelIdSchema,
  StringBooleanSchema,
  UUIDSchema,
} from '../common/schemas';
import { enumLabels } from '../common/fields';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { STREAM_TYPE_LABELS, StreamType } from './enums';
import { DBLessonTemplateLessonIdSchema } from '../lessonTemplates/schemas';
import { DBPermissionPermissionIdSchema } from '../permissions/schemas';
import { DBPricePriceIdSchema } from '../prices/schemas';

/**
 * Shared & Helper Schemas
 */
export const DBStreamCourseStreamIdSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.lazy(() => DBCourseCourseIdSchema).safeParse(parts[0]).success &&
    DBStreamStreamIdSchema.safeParse(parts[1]).success
  );
});

/**
 * DB Schemas
 */

export const DBStreamCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBStreamStreamIdSchema = UUIDSchema;
export const DBStreamActiveSchema = StringBooleanSchema;
export const DBStreamBasePermissionsSchema = z.record(
  z.lazy(() => DBPermissionPermissionIdSchema),
  z.object({}).strict()
);
export const DBStreamGradYearSchema = GradYearSchema.openapi({
  description: 'Year the cohort taking this stream graduates',
  example: 2027,
});
export const GENERAL_GROUP_NAME = 'General';

export const DBStreamGroupNameSchema = AllCharLabelSchema.min(1, {
  message: 'please enter a group',
})
  .max(64, { message: 'is too long, use 64 characters or fewer' })
  .transform(group =>
    group.trim().toLowerCase() === GENERAL_GROUP_NAME.toLowerCase() ? GENERAL_GROUP_NAME : group
  )
  .openapi({
    description: 'Group the stream serves, such as a campus, a school, or General',
    example: 'Chatswood',
  });
export const DBStreamOrderedLessonsSchema = z.array(z.lazy(() => DBLessonTemplateLessonIdSchema));
export const DBStreamPriceIdSchema = z.lazy(() => DBPricePriceIdSchema);
// Remove trailing slash and http/https
export const DBStreamScheduleLinkSchema = HttpUrlSchema.transform(str =>
  str.replace(/^(http:\/\/|https:\/\/)|\/$/g, '')
)
  .pipe(z.string().max(256))
  .openapi({
    description: 'Link to the schedules page for the stream, included in invoices etc.',
    example: 'cognitotuition.com/schedule',
  });
export const DBStreamSeparatelyBilledSchema = z.boolean();
export const DBStreamGroupCodeSchema = AlphanumericLabelSchema.min(1, {
  message: 'please enter a short form for the group',
})
  .max(12, { message: 'is too long, use 12 characters or fewer' })
  .transform(group =>
    group.trim().toLowerCase() === GENERAL_GROUP_NAME.toLowerCase() ? GENERAL_GROUP_NAME : group
  )
  .openapi({
    description: "Group's short form, as it appears in a stream's short label",
    example: 'CHS',
  });
export const DBStreamSlackIdsSchema = z.record(
  SlackChannelIdSchema,
  z.object({
    label: z.string().min(1).max(256),
  })
);
export const DBStreamTypeSchema = z
  .enum(StreamType, { message: 'please choose standard, accelerated or holiday' })
  .openapi({
    description: 'How the stream runs',
    example: StreamType.Accelerated,
  })
  .register(enumLabels, { labels: STREAM_TYPE_LABELS });

export const DBStreamSchema = z.object({
  course: DBStreamCourseSchema,
  streamId: DBStreamStreamIdSchema,
  active: DBStreamActiveSchema,
  // .optional() indicates to ElectroDB that keys of this dynamic field may be deleted
  basePermissions: DBStreamBasePermissionsSchema.optional(),
  gradYear: DBStreamGradYearSchema,
  groupName: DBStreamGroupNameSchema,
  groupCode: DBStreamGroupCodeSchema,
  orderedLessons: DBStreamOrderedLessonsSchema,
  priceId: DBStreamPriceIdSchema.optional(),
  scheduleLink: DBStreamScheduleLinkSchema,
  separatelyBilled: DBStreamSeparatelyBilledSchema,
  slackIds: DBStreamSlackIdsSchema,
  type: DBStreamTypeSchema,
});

/**
 * Client Schemas
 */

export const StreamCourseSchema = DBStreamCourseSchema;
export const StreamStreamIdSchema = DBStreamStreamIdSchema;
export const StreamActiveSchema = z.boolean();
export const StreamBasePermissionsSchema = z.array(z.lazy(() => DBPermissionPermissionIdSchema));
export const StreamGradYearSchema = DBStreamGradYearSchema;
export const StreamGroupNameSchema = DBStreamGroupNameSchema;
export const StreamGroupCodeSchema = DBStreamGroupCodeSchema;
export const StreamOrderedLessonsSchema = DBStreamOrderedLessonsSchema;
export const StreamPriceIdSchema = DBStreamPriceIdSchema;
export const StreamScheduleLinkSchema = DBStreamScheduleLinkSchema;
export const StreamSeparatelyBilledSchema = DBStreamSeparatelyBilledSchema;
export const StreamSlackIdsSchema = z.array(
  z.object({
    label: z.string().min(1).max(256),
    value: SlackChannelIdSchema,
  })
);
export const StreamTypeSchema = DBStreamTypeSchema;

export const StreamSchema = z.object({
  streamId: StreamStreamIdSchema.meta({ title: 'Stream ID' }),
  course: StreamCourseSchema.meta({ title: 'Course' }),
  active: StreamActiveSchema.default(true).meta({ title: 'Active' }),
  basePermissions: StreamBasePermissionsSchema.default([]).meta({ title: 'Base permissions' }),
  gradYear: StreamGradYearSchema.meta({ title: 'Grad year' }),
  groupName: StreamGroupNameSchema.default(GENERAL_GROUP_NAME).meta({ title: 'Group name' }),
  // Composed by the codec from the fields above rather than stored. The default
  // keeps them off the write path while leaving a plain string to read back.
  label: z.string().default(''),
  shortLabel: z.string().default(''),
  groupCode: StreamGroupCodeSchema.default(GENERAL_GROUP_NAME).meta({ title: 'Group code' }),
  orderedLessons: StreamOrderedLessonsSchema.default([]).meta({ title: 'Lessons' }),
  priceId: StreamPriceIdSchema.optional().meta({ title: 'Price' }),
  scheduleLink: StreamScheduleLinkSchema.meta({ title: 'Schedule link' }),
  separatelyBilled: StreamSeparatelyBilledSchema.default(false).meta({
    title: 'Separately billed',
  }),
  slackIds: StreamSlackIdsSchema.default([]).meta({ title: 'Slack Ids' }),
  type: StreamTypeSchema.meta({ title: 'Type' }),
});
